import { useRef } from 'react';
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from 'framer-motion';

// Infinite horizontal photography strip. Base drift is constant; scrolling
// speeds it up and reverses direction on scroll-up.
export const ImageMarquee = ({ items = [], baseSpeed = 26, reverse = false, height = 280 }) => {
  const x = useMotionValue(0);
  const { scrollY } = useScroll();
  const velocity = useVelocity(scrollY);
  const smooth = useSpring(velocity, { damping: 48, stiffness: 380 });
  const factor = useTransform(smooth, [-1600, 0, 1600], [-4, 1, 4], { clamp: false });
  const dirRef = useRef(reverse ? -1 : 1);
  const trackRef = useRef(null);

  useAnimationFrame((_, delta) => {
    const f = factor.get();
    if (f < 0) dirRef.current = reverse ? 1 : -1;
    if (f > 0) dirRef.current = reverse ? -1 : 1;
    const width = trackRef.current ? trackRef.current.offsetWidth / 2 : 1;
    let next = x.get() + dirRef.current * baseSpeed * Math.abs(f) * (delta / 1000);
    if (next <= -width) next += width;
    if (next >= 0) next -= width;
    x.set(next);
  });

  if (!items.length) return null;
  const loop = [...items, ...items];

  return (
    <div className="overflow-hidden select-none" data-testid="image-marquee">
      <motion.div ref={trackRef} className="flex gap-5 w-max" style={{ x }}>
        {loop.map((item, i) => (
          <div
            key={`${item.code}-${i}`}
            className="relative shrink-0 bg-mb-surface hairline border-subtle overflow-hidden"
            style={{ height, width: height * 0.82 }}
          >
            <img
              src={item.image}
              alt={item.name || item.code}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
              loading="lazy"
            />
            <div className="absolute left-3 bottom-3 eyebrow-sm text-gold bg-mb/80 px-2 py-1">
              {item.code}
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export default ImageMarquee;
